import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Lightweight analytics hook — pushes to dataLayer / gtag if present. Never sends PII.
function track(event, props = {}) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...props });
    if (typeof window.gtag === "function") window.gtag("event", event, props);
  } catch { /* ignore */ }
}

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function useSiteMeta() {
  useEffect(() => {
    const prev = document.title;
    document.title = "Maxx Madison | Author of House Diamond";
    const metas = [
      { name: "description", content: "Enter the world of House Diamond, a luxury underworld thriller from Maxx Madison. Join the private reader list for excerpts, publication news, and early-access opportunities." },
      { property: "og:title", content: "House Diamond by Maxx Madison" },
      { property: "og:description", content: "Beauty opens the door. What happens inside is something else entirely." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "House Diamond by Maxx Madison" },
      { name: "twitter:description", content: "Beauty opens the door. What happens inside is something else entirely." },
    ];
    const created = [];
    metas.forEach((m) => {
      const el = document.createElement("meta");
      Object.entries(m).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute("data-landing", "1");
      document.head.appendChild(el);
      created.push(el);
    });
    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.href = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400&family=Inter:wght@300;400;500;600&display=swap";
    font.setAttribute("data-landing", "1");
    document.head.appendChild(font);
    created.push(font);
    return () => { document.title = prev; created.forEach((e) => e.remove()); };
  }, []);
}

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`relative mx-auto w-full max-w-5xl px-6 sm:px-10 ${className}`}>{children}</section>
);

function CoverArt({ url }) {
  if (url) {
    return (
      <img
        src={url}
        alt="House Diamond cover artwork"
        loading="eager"
        className="mx-auto w-full max-w-[340px] rounded-sm object-cover shadow-2xl ring-1 ring-[#c9a227]/25"
        style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.8)" }}
      />
    );
  }
  return (
    <div
      data-testid="cover-placeholder"
      className="mx-auto flex aspect-[2/3] w-full max-w-[340px] flex-col items-center justify-center rounded-sm border border-[#c9a227]/30 bg-gradient-to-b from-[#141210] to-[#0c0b0a] p-8 text-center shadow-2xl"
      style={{ boxShadow: "0 30px 80px -20px rgba(0,0,0,0.8)" }}
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#c9a227]/70">Maxx Madison</div>
      <div className="mt-6 font-[Cormorant_Garamond] text-4xl font-medium leading-none tracking-wide text-[#efe7d9]">HOUSE<br />DIAMOND</div>
      <div className="mt-6 h-px w-12 bg-[#c9a227]/50" />
      <div className="mt-6 font-mono text-[9px] uppercase tracking-[0.3em] text-[#efe7d9]/35">Cover artwork placeholder</div>
    </div>
  );
}

export default function LandingPage() {
  useSiteMeta();
  const [cfg, setCfg] = useState({ contact_email: null, cover_url: null, current_year: new Date().getFullYear() });
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const startedRef = useRef(false);

  useEffect(() => {
    track("page_view", { page: "author_landing" });
    fetch(`${BACKEND}/api/site/config`).then((r) => r.json()).then((d) => setCfg((c) => ({ ...c, ...d }))).catch(() => {});
  }, []);

  const onFocusForm = useCallback(() => {
    if (!startedRef.current) { startedRef.current = true; track("form_start"); }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "submitting") return;
    setState("submitting");
    try {
      const res = await fetch(`${BACKEND}/api/subscribers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), first_name: form.first_name.trim() || null, source: "author_landing_page" }),
      });
      if (!res.ok) throw new Error("bad");
      setState("success");
      track("signup_success");
    } catch {
      setState("error");
    }
  };

  const goForm = (evt) => { track("hero_cta_click", { cta: evt }); scrollToId("join"); };

  return (
    <div className="min-h-screen bg-[#0b0a09] font-[Inter] text-[#efe7d9] antialiased" style={{ scrollBehavior: "smooth" }}>
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-white/5 bg-[#0b0a09]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4 sm:px-10">
          <button onClick={() => scrollToId("hero")} data-testid="nav-brand" className="shrink-0 whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.18em] text-[#efe7d9] transition-colors hover:text-[#c9a227] sm:text-xs sm:tracking-[0.32em]">Maxx&nbsp;Madison</button>
          <nav className="flex items-center gap-4 text-xs sm:gap-8">
            <button onClick={() => scrollToId("hero")} className="hidden font-[Cormorant_Garamond] text-base italic text-[#efe7d9]/70 transition-colors hover:text-[#c9a227] sm:inline">House Diamond</button>
            <button onClick={() => scrollToId("about")} data-testid="nav-about" className="uppercase tracking-[0.18em] text-[#efe7d9]/70 transition-colors hover:text-[#c9a227]">About</button>
            <button onClick={() => goForm("header")} data-testid="nav-join" className="rounded-full border border-[#c9a227]/50 px-4 py-1.5 uppercase tracking-[0.18em] text-[#c9a227] transition-colors hover:bg-[#c9a227]/10">Join the List</button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <Section id="hero" className="grid items-center gap-12 pt-16 pb-24 sm:pt-24 md:grid-cols-2 md:gap-16">
        <div className="order-2 md:order-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#c9a227]/80">A Novel</div>
          <h1 className="mt-4 font-[Cormorant_Garamond] text-5xl font-medium leading-[0.95] tracking-tight text-[#efe7d9] sm:text-6xl">HOUSE DIAMOND</h1>
          <p className="mt-6 font-[Cormorant_Garamond] text-xl italic leading-snug text-[#c9a227] sm:text-2xl">Beauty opens the door. What happens inside is something else entirely.</p>
          <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#efe7d9]/75">
            <p>Julian Mercer knows the effect he has on people. He has simply never imagined that beauty could be trained, disciplined, and transformed into something powerful enough to alter another person&rsquo;s life.</p>
            <p>Then an invitation draws him into a private world built for those wealthy enough to commission what ordinary life cannot provide.</p>
            <p>Inside House Diamond, desire is not improvised. It is studied. Curated. Perfected.</p>
            <p>And Julian may have been made for it.</p>
          </div>
          <div className="mt-9">
            <button onClick={() => goForm("hero")} data-testid="hero-cta" className="group inline-flex items-center gap-3 rounded-full bg-[#c9a227] px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#0b0a09] transition-transform duration-300 hover:scale-[1.02] hover:bg-[#d8b23a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a227] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0a09]">
              Enter the House
              <span className="transition-transform duration-300 group-hover:translate-x-1">&rarr;</span>
            </button>
            <p className="mt-4 font-[Cormorant_Garamond] text-base italic text-[#efe7d9]/50">A novel by Maxx Madison</p>
          </div>
        </div>
        <div className="order-1 md:order-2">
          <CoverArt url={cfg.cover_url} />
        </div>
      </Section>

      <div className="mx-auto h-px w-full max-w-5xl bg-gradient-to-r from-transparent via-[#c9a227]/25 to-transparent" />

      {/* Positioning */}
      <Section id="about" className="py-24">
        <div className="max-w-2xl">
          <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#c9a227]/70">A luxury underworld thriller</div>
          <h2 className="mt-4 font-[Cormorant_Garamond] text-4xl font-medium tracking-tight text-[#efe7d9] sm:text-5xl">A WORLD BUILT AROUND DESIRE</h2>
          <div className="mt-6 space-y-4 text-[15px] leading-relaxed text-[#efe7d9]/75">
            <p>Behind the familiar world exists another, accessible only by invitation.</p>
            <p>Its clients do not purchase time. They commission experiences. Its practitioners are selected, trained, and organized into four Houses, each devoted to a different dimension of human desire.</p>
            <p>House Diamond begins with physical perfection. It becomes a story about power, intimacy, ambition, and what it means to be truly seen.</p>
          </div>
        </div>
      </Section>

      {/* Mailing list */}
      <div className="relative border-y border-[#c9a227]/10 bg-gradient-to-b from-[#100d0b] to-[#0b0a09]">
        <Section id="join" className="py-24">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="font-[Cormorant_Garamond] text-4xl font-medium tracking-tight text-[#efe7d9] sm:text-5xl">BE AMONG THE FIRST INVITED</h2>
            <p className="mt-5 text-[15px] leading-relaxed text-[#efe7d9]/75">Join Maxx Madison&rsquo;s private reader list for publication news, early excerpts, cover reveals, and opportunities to read or listen before release.</p>

            {state === "success" ? (
              <div data-testid="signup-success" role="status" className="mx-auto mt-10 max-w-md rounded-md border border-[#c9a227]/30 bg-[#c9a227]/5 px-6 py-8">
                <p className="font-[Cormorant_Garamond] text-2xl italic text-[#c9a227]">You&rsquo;re on the list.</p>
                <p className="mt-3 text-sm leading-relaxed text-[#efe7d9]/70">The door is not open yet, but you&rsquo;ll be among the first to know when it is.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="signup-form" className="mx-auto mt-10 max-w-md text-left" noValidate>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="first_name" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#efe7d9]/50">First name <span className="text-[#efe7d9]/30">(optional)</span></label>
                    <input
                      id="first_name" type="text" autoComplete="given-name" value={form.first_name}
                      onFocus={onFocusForm}
                      onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                      data-testid="signup-first-name"
                      className="w-full rounded-md border border-white/10 bg-[#0b0a09] px-4 py-3 text-[15px] text-[#efe7d9] outline-none transition-colors placeholder:text-[#efe7d9]/25 focus:border-[#c9a227]/60 focus-visible:ring-1 focus-visible:ring-[#c9a227]/40"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-[#efe7d9]/50">Email address <span className="text-[#c9a227]/70">*</span></label>
                    <input
                      id="email" type="email" required autoComplete="email" value={form.email}
                      onFocus={onFocusForm}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      data-testid="signup-email"
                      className="w-full rounded-md border border-white/10 bg-[#0b0a09] px-4 py-3 text-[15px] text-[#efe7d9] outline-none transition-colors placeholder:text-[#efe7d9]/25 focus:border-[#c9a227]/60 focus-visible:ring-1 focus-visible:ring-[#c9a227]/40"
                      placeholder="you@example.com"
                    />
                  </div>
                  {state === "error" && (
                    <p data-testid="signup-error" role="alert" className="text-sm text-[#e08a8a]">We couldn&rsquo;t add you to the list. Please check your email address and try again.</p>
                  )}
                  <button
                    type="submit" data-testid="signup-submit" disabled={state === "submitting"}
                    className="w-full rounded-full bg-[#c9a227] px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#0b0a09] transition-transform duration-300 hover:scale-[1.01] hover:bg-[#d8b23a] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a227] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0b0a09] disabled:opacity-60"
                  >
                    {state === "submitting" ? "Joining\u2026" : "Join the Private List"}
                  </button>
                  <p className="text-center text-[11px] leading-relaxed text-[#efe7d9]/40">By joining, you agree to receive occasional emails from Maxx Madison. Unsubscribe at any time. Your information will not be sold or shared.</p>
                </div>
              </form>
            )}
          </div>
        </Section>
      </div>

      {/* Author */}
      <Section id="author" className="py-24">
        <div className="grid items-center gap-10 md:grid-cols-[200px_1fr]">
          <div data-testid="author-photo" className="mx-auto flex aspect-square w-40 items-center justify-center rounded-full border border-[#c9a227]/25 bg-gradient-to-b from-[#141210] to-[#0c0b0a] md:mx-0">
            <span className="font-[Cormorant_Garamond] text-4xl text-[#c9a227]/40">MM</span>
          </div>
          <div>
            <h2 className="font-[Cormorant_Garamond] text-4xl font-medium tracking-tight text-[#efe7d9] sm:text-5xl">MAXX MADISON</h2>
            <div className="mt-5 space-y-4 text-[15px] leading-relaxed text-[#efe7d9]/75">
              <p>Maxx Madison writes provocative fiction about desire, identity, intimacy, and the private worlds people build around the things they want most.</p>
              <p>House Diamond is his debut novel.</p>
            </div>
          </div>
        </div>
      </Section>

      {/* Closing */}
      <div className="relative overflow-hidden border-t border-white/5 bg-[#0c0b0a]">
        <Section className="py-28 text-center">
          <h2 className="mx-auto max-w-3xl font-[Cormorant_Garamond] text-4xl font-medium leading-tight tracking-tight text-[#efe7d9] sm:text-5xl">NOT EVERY DOOR IS MEANT TO REMAIN CLOSED.</h2>
          <button onClick={() => goForm("closing")} data-testid="closing-cta" className="mt-10 inline-flex items-center gap-3 rounded-full border border-[#c9a227]/60 px-8 py-3.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#c9a227] transition-colors duration-300 hover:bg-[#c9a227]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a227] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c0b0a]">
            Join the Reader List
          </button>
        </Section>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#0b0a09]">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-4 px-6 py-8 text-xs text-[#efe7d9]/45 sm:flex-row sm:px-10">
          <div>&copy; {cfg.current_year} Maxx Madison. All rights reserved.</div>
          <nav className="flex items-center gap-6">
            <a href="/privacy" data-testid="footer-privacy" className="uppercase tracking-[0.18em] transition-colors hover:text-[#c9a227]">Privacy</a>
            {cfg.contact_email && (
              <a href={`mailto:${cfg.contact_email}`} data-testid="footer-contact" className="uppercase tracking-[0.18em] transition-colors hover:text-[#c9a227]">Contact</a>
            )}
          </nav>
        </div>
      </footer>

      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration: 0.001ms !important; transition-duration: 0.001ms !important; scroll-behavior: auto !important; }
        }
      `}</style>
    </div>
  );
}
